import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './NotFound.css';

const COLS = 20;
const ROWS = 14;
const RADIUS = 120; // zone d'influence de la souris (px)

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const gridRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef<{ x: number; y: number }>({ x: -9999, y: -9999 });
  const rafRef = useRef<number>(0);

  const animateDots = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const dots = grid.querySelectorAll<HTMLSpanElement>('.nf-dot');
    const { x: mx, y: my } = mouseRef.current;

    dots.forEach((dot) => {
      const rect = dot.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = mx - cx;
      const dy = my - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < RADIUS) {
        const force = (1 - dist / RADIUS) * 10; // max 10px de déplacement
        const tx = -(dx / dist) * force;
        const ty = -(dy / dist) * force;
        const scale = 1 + (1 - dist / RADIUS) * 1.2;
        dot.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
        dot.style.opacity = String(0.25 + (1 - dist / RADIUS) * 0.75);
        dot.style.background = `var(--skin-color)`;
      } else {
        dot.style.transform = 'translate(0,0) scale(1)';
        dot.style.opacity = '0.18';
        dot.style.background = '';
      }
    });

    rafRef.current = requestAnimationFrame(animateDots);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    const onLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 };
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);
    rafRef.current = requestAnimationFrame(animateDots);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafRef.current);
    };
  }, [animateDots]);

  return (
    <div className="notfound">
      {/* Dot grid */}
      <div className="notfound__dotgrid" ref={gridRef} aria-hidden="true">
        {Array.from({ length: COLS * ROWS }).map((_, i) => (
          <span key={i} className="nf-dot" />
        ))}
      </div>

      {/* Soft background blobs */}
      <div className="notfound__shapes" aria-hidden="true">
        <span className="notfound__shape" />
        <span className="notfound__shape" />
        <span className="notfound__shape" />
        <span className="notfound__shape" />
      </div>

      <div className="notfound__content">
        {/* Illustration */}
        <div className="notfound__illustration">
          <span className="notfound__char" role="img" aria-label="personnage perdu">🙈</span>
          <div className="notfound__number" aria-label="Erreur 404">404</div>
        </div>

        <p className="notfound__tag">Oups !</p>

        <h1 className="notfound__title">On s'est perdus...</h1>

        <p className="notfound__subtitle">
          La page que tu cherches n'existe pas ou a été déplacée.
          Pas de panique, l'accueil est juste là 👇
        </p>

        <div className="notfound__btn-wrap">
          <button className="notfound__btn" onClick={() => navigate('/')}>
            <span className="notfound__btn-icon">←</span>
            Retour à l'accueil
          </button>
        </div>

        <p className="notfound__tip">Tu peux aussi appuyer sur la touche Retour de ton navigateur</p>
      </div>
    </div>
  );
};

export default NotFound;
